import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
} from '@chakra-ui/react';
import {
  InquiryWithContext,
  inquiryApi,
} from '@texas/api/endpoints/inquiryApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inquiryEvents } from './events';
import { InquiryHistoryModal } from './InquiryHistoryModal';
import { canAcceptInquiry, InquiryStatusDialog } from './InquiryStatusDialog';

interface Props {
  inquiry: InquiryWithContext;
}

export function InquiryRowActions({ inquiry }: Props) {
  const { t } = useTranslation();
  const onChanged = () =>
    inquiryEvents.onVariantUpdated.dispatch(inquiry.variantId);
  const [historyInquiry, setHistoryInquiry] =
    useState<InquiryWithContext | null>(null);
  const { request: archiveRequest, loading: archiving } = useApiRequest(
    inquiryApi.archiveInquiry,
  );
  const { request: restoreRequest, loading: restoring } = useApiRequest(
    inquiryApi.restoreInquiry,
  );
  const { request: setMainRequest, loading: settingMain } = useApiRequest(
    inquiryApi.setMainInquiry,
  );

  const {
    value: inquiryToCopy,
    isOpen: isCopyOpen,
    onOpen: onCopyOpen,
    onClose: onCopyClose,
  } = useValueDisclosure<InquiryWithContext>();
  const {
    value: inquiryToAccept,
    isOpen: isAcceptOpen,
    onOpen: onAcceptOpen,
    onClose: onAcceptClose,
  } = useValueDisclosure<InquiryWithContext>();
  const {
    value: inquiryToReject,
    isOpen: isRejectOpen,
    onOpen: onRejectOpen,
    onClose: onRejectClose,
  } = useValueDisclosure<InquiryWithContext>();

  return (
    <>
      <Menu isLazy={true}>
        <MenuButton
          as={IconButton}
          aria-label={t('general.more')}
          icon={<Icons.DotsHorizontal boxSize={defaultIconSize} />}
          isLoading={archiving || restoring || settingMain}
          size="xs"
          variant="texas-light"
        />
        <Portal>
          <MenuList>
            {!inquiry.archived && (
              <>
                <MenuItem
                  icon={<Icons.Checkmark />}
                  isDisabled={!canAcceptInquiry(inquiry)}
                  onClick={() => onAcceptOpen(inquiry)}
                >
                  {t('inquiry.accept')}
                </MenuItem>
                <MenuItem
                  icon={<Icons.Close />}
                  onClick={() => onRejectOpen(inquiry)}
                >
                  {t('inquiry.reject')}
                </MenuItem>
              </>
            )}
            <MenuItem
              icon={<Icons.History />}
              onClick={() => setHistoryInquiry(inquiry)}
            >
              {t('history.viewHistory')}
            </MenuItem>
            <MenuItem
              icon={<Icons.Flag />}
              isDisabled={
                !inquiry.acceptedDate ||
                inquiry.isMain ||
                !!inquiry.controlledById
              }
              onClick={async () => {
                const response = await setMainRequest(inquiry.id);
                if (!response.hasError) onChanged();
              }}
            >
              {t('inquiry.setMain')}
            </MenuItem>
            <MenuItem
              icon={
                inquiry.archived ? <Icons.ArchiveOutline /> : <Icons.Archive />
              }
              isDisabled={!!inquiry.acceptedDate && !inquiry.archived}
              onClick={async () => {
                const response = inquiry.archived
                  ? await restoreRequest(inquiry.id)
                  : await archiveRequest(inquiry.id);
                if (!response.hasError) onChanged();
              }}
            >
              {inquiry.archived ? t('inquiry.restore') : t('inquiry.archive')}
            </MenuItem>
            <MenuItem
              icon={<Icons.ContentCopy />}
              onClick={() => onCopyOpen(inquiry)}
            >
              {t('inquiry.copy')}
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>

      {historyInquiry && (
        <InquiryHistoryModal
          inquiryId={historyInquiry.id}
          isOpen={true}
          name={historyInquiry.formattedInquiryNumber}
          onClose={() => setHistoryInquiry(null)}
        />
      )}
      {inquiryToCopy && (
        <VerifyDialogWithRequest
          key={inquiryToCopy.id}
          args={[inquiryToCopy.id]}
          headerTitle={t('inquiry.confirmCopyHeader')}
          isOpen={isCopyOpen}
          onClose={onCopyClose}
          onPerformed={onChanged}
          onSuccessTitle={t('inquiry.copied')}
          primaryButtonTitle={t('inquiry.copy')}
          primaryRequest={inquiryApi.copyInquiry}
          secondaryButtonTitle={t('general.cancel')}
        >
          {t('inquiry.confirmCopy')}
        </VerifyDialogWithRequest>
      )}
      {inquiryToAccept && (
        <InquiryStatusDialog
          action="accept"
          inquiry={inquiryToAccept}
          isOpen={isAcceptOpen}
          onClose={onAcceptClose}
          onPerformed={onChanged}
          primaryRequest={inquiryApi.acceptInquiry}
        />
      )}
      {inquiryToReject && (
        <InquiryStatusDialog
          action="reject"
          inquiry={inquiryToReject}
          isOpen={isRejectOpen}
          onClose={onRejectClose}
          onPerformed={onChanged}
          primaryRequest={inquiryApi.rejectInquiry}
        />
      )}
    </>
  );
}
